import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from './SignatureModal';

interface EulaAgreementProps {
  onAccept: (signature: string) => void;
}

const EulaAgreement: React.FC<EulaAgreementProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [scrollAnnouncement, setScrollAnnouncement] = useState('');

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
      if (isAtBottom && !hasScrolledToBottom) {
        setScrollAnnouncement('You have reached the end of the agreement. You may now accept the terms.');
      }
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            My ClinicCore
          </h1>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            Client Portal End User License Agreement
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the agreement below and scroll to the bottom to continue
          </p>
        </div>

        {/* EULA Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            role="textbox"
            aria-label="End User License Agreement text"
            aria-readonly="true"
            tabIndex={0}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              END USER LICENSE AGREEMENT
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              This End User License Agreement ("Agreement") is entered into between You and My ClinicCore. This Agreement governs your use of the My ClinicCore client portal and related services.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              1. ACCEPTANCE OF TERMS
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              By accessing or using the My ClinicCore client portal, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions of this Agreement. If you do not agree to these terms, you may not use our services.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              2. DESCRIPTION OF SERVICE
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              My ClinicCore provides a secure online portal that allows patients to access their medical information, complete forms, schedule appointments, and communicate with healthcare providers. The service is designed to facilitate healthcare delivery and improve patient-provider communication.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              3. USER RESPONSIBILITIES
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              As a user of our portal, you agree to:
            </p>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
              <li>Provide accurate and complete information</li>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Use the portal only for lawful purposes</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              4. PRIVACY AND SECURITY
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              We are committed to protecting your privacy and the security of your health information. Our Privacy Policy, which is incorporated by reference into this Agreement, describes how we collect, use, and protect your personal and health information in compliance with HIPAA and other applicable privacy laws.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              5. INTELLECTUAL PROPERTY
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              All content, features, and functionality of the My ClinicCore portal, including but not limited to text, graphics, logos, software, and design, are owned by My ClinicCore or its licensors and are protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              6. LIMITATION OF LIABILITY
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              To the maximum extent permitted by law, My ClinicCore shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the portal, including but not limited to damages for loss of profits, goodwill, use, data, or other intangible losses.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              7. TERMINATION
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              We may terminate or suspend your access to the portal at any time, with or without cause, and with or without notice. Upon termination, your right to use the portal ceases immediately, but provisions regarding intellectual property, limitation of liability, and governing law shall survive termination.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              8. MODIFICATIONS
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              We reserve the right to modify this Agreement at any time. We will notify you of any material changes by posting the updated Agreement on our portal. Your continued use of the portal after such modifications constitutes your acceptance of the updated terms.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              9. GOVERNING LAW
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              This Agreement shall be governed by and construed in accordance with the laws of the state in which My ClinicCore is located, without regard to its conflict of law principles.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              10. CONTACT INFORMATION
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              If you have any questions about this Agreement, please contact us at:
              <br />
              My ClinicCore
              <br />
              Email: support@mycliniccore.com
              <br />
              Phone: (555) 123-4567
            </p>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb' }}>
              By checking the agreement box and providing your digital signature below, you acknowledge that you have read, understood, and agree to be bound by this End User License Agreement.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Screen reader announcement for scroll status */}
        <div aria-live="polite" aria-atomic="true" className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          {scrollAnnouncement}
        </div>
        {!hasScrolledToBottom && (
          <p style={{ color: '#D4A017', fontSize: '0.85rem', textAlign: 'center', marginBottom: '0.5rem' }}>
            Please scroll to the bottom of the agreement to enable the checkbox below.
          </p>
        )}

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
              minHeight: '44px'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                aria-describedby={!hasScrolledToBottom ? 'eula-scroll-hint' : undefined}
                style={{
                  marginRight: '0.75rem',
                  width: '20px',
                  height: '20px',
                  minWidth: '20px',
                  minHeight: '20px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                  accentColor: '#D4A017'
                }}
              />
              I have read, understood, and agree to the End User License Agreement
            </label>
            {!hasScrolledToBottom && (
              <p id="eula-scroll-hint" style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.25rem', marginLeft: '2rem' }}>
                Scroll to the bottom of the agreement above to enable this checkbox.
              </p>
            )}
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
            onMouseEnter={(e) => {
              if (signature) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(212, 160, 23, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (signature) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(212, 160, 23, 0.3)';
              }
            }}
          >
            Submit Agreement
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Agreement
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="EULA Digital Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default EulaAgreement;